import { ClientJS } from "clientjs";
import UAParser from "ua-parser-js";
import { sha256 } from "js-sha256";
import { apiHelper } from "./modules/util";
import { getVid, getUid, getXsollauid } from "./modules/cookies";
import { send, sendXHR, setXsollauidFromUrl } from "./modules/request";
import {
  EventProperties,
  transferDataToIframe,
  setHandlers,
} from "./modules/events";
import { isSafari17 } from "./modules/browser";

/**
 * Initializes the Xsolla Analytics module.
 * @param {object} params - The configuration parameters for Xsolla Analytics.
 * @param {number} params.id - The counter ID for Xsolla Analytics.
 * @param {string[]} params.siteDomains - The domains associated with the site.
 * @param {string} [params.server] - The server URL for Xsolla Analytics.
 * @param {number} [params.externalId] - The external ID for Xsolla Analytics.
 * @param {number} [params.merchantId] - The merchant ID for Xsolla Analytics.
 * @param {object} [params.extraValues] - Additional values to be included in the requests.
 * @param {boolean} [params.hit] - Whether to track user actions.
 * @param {boolean} [params.extLink] - Whether to track external links.
 * @param {boolean} [params.click] - Whether to track element clicks.
 * @param {boolean} [params.scroll] - Whether to track scroll events.
 * @param {boolean} [params.sendLoadMetrics] - Whether to send load metrics.
 * @param {object} [params.abParams] - AB testing parameters.
 */
export default function XsollaAnalytics(params) {
  const Config = {
    counterId: params.id,
    clientId: 0,
    visitId: 0,
    siteDomains: params.siteDomains,
    server:
      params.server.match(/(https?:\/\/[^\/]*)/g)?.[0] ||
      "https://minimetrika.xsolla.com",
    hitUrl: "",
    externalId: params.externalId || params.merchantId,
    extraValues: params.extraValues ? JSON.stringify(params.extraValues) : "",
    hit: typeof params.hit !== "undefined" ? params.hit : true,
    extLink: typeof params.extLink !== "undefined" ? params.extLink : true,
    click: typeof params.click !== "undefined" ? params.click : true,
    scroll: typeof params.scroll !== "undefined" ? params.scroll : true,
    sendLoadMetrics:
      typeof params.sendLoadMetrics !== "undefined"
        ? params.sendLoadMetrics
        : false,
    abParams: params.abParams ? JSON.stringify(params.abParams) : "",
    isBrowser: typeof window !== "undefined",
    hitCount: 0,
    dfp: null,
    keyDfp: null,
    ua: navigator.userAgent,
  };

  if (Config.isBrowser) {
    EventProperties.firstHiddenTime =
      document.visibilityState === "hidden" ? 0 : Infinity;
  }

  if (Config.isBrowser) {
    const client = new ClientJS();
    const parser = new UAParser();
    const browserData = parser.getResult();
    const browser = browserData.browser;
    Config.browserName = browser.name;
    Config.browserMajor = browser.major;
    Config.ua = navigator.userAgent;
    const safari17 = isSafari17();

    const key = {
      userAgent: browserData.ua,
      screenPrint: safari17 ? undefined : client.getScreenPrint(),
      pluginList: client.getPlugins(),
      fontList: client.getFonts(),
      localStorage: client.isLocalStorage(),
      sessionStorage: client.isSessionStorage(),
      timeZone: client.getTimeZone(),
      language: client.getLanguage(),
      systemLanguage: client.getSystemLanguage(),
      cookies: client.isCookie(),
      canvasPrint: safari17 ? undefined : client.getCanvasPrint(),
      getBrowserData: client.getBrowserData(),
      getBrowser: client.getBrowser(),
      getBrowserVersion: client.getBrowserVersion(),
      getBrowserMajorVersion: client.getBrowserMajorVersion(),
      isIE: client.isIE(),
      isChrome: client.isChrome(),
      isFirefox: client.isFirefox(),
      isSafari: client.isSafari(),
      isOpera: client.isOpera(),
      getEngine: client.getEngine(),
      getEngineVersion: client.getEngineVersion(),
      getOS: client.getOS(),
      getOSVersion: client.getOSVersion(),
      isWindows: client.isWindows(),
      isMac: client.isMac(),
      isLinux: client.isLinux(),
      isUbuntu: client.isUbuntu(),
      isSolaris: client.isSolaris(),
      getDevice: client.getDevice(),
      getDeviceType: client.getDeviceType(),
      getDeviceVendor: client.getDeviceVendor(),
      getCPU: client.getCPU(),
      isMobile: client.isMobile(),
      isMobileMajor: client.isMobileMajor(),
      isMobileAndroid: client.isMobileAndroid(),
      isMobileOpera: client.isMobileOpera(),
      isMobileWindows: client.isMobileWindows(),
      isMobileBlackBerry: client.isMobileBlackBerry(),
      isMobileIOS: client.isMobileIOS(),
      isIphone: client.isIphone(),
      isIpad: client.isIpad(),
      getColorDepth: client.getColorDepth(),
      getCurrentResolution: safari17
        ? undefined
        : client.getCurrentResolution(),
      getAvailableResolution: safari17
        ? undefined
        : client.getAvailableResolution(),
      getDeviceXDPI: client.getDeviceXDPI(),
      getDeviceYDPI: client.getDeviceYDPI(),
      isJava: client.isJava(),
      isFlash: client.isFlash(),
      isSilverlight: client.isSilverlight(),
      getSilverlightVersion: client.getSilverlightVersion(),
      getMimeTypes: client.getMimeTypes(),
      isMimeTypes: client.isMimeTypes(),
      isFont: client.isFont(),
      isCanvas: client.isCanvas(),
    };

    Config.keyDfp = key;
    Config.dfp = sha256(JSON.stringify(key));
  }

  /**
   * Sends a request to track user actions.
   * @param {string} url - The address to which the request will be sent.
   * @param {object} obj - Additional data to be included in the request.
   */
  const hit = (url, obj) => {
    if (!Config.isBrowser) {
      return;
    }

    const dataObj = apiHelper(
      Config.externalId,
      Config.extraValues,
      Config.abParams,
      1,
      obj
    );

    const ref =
      window.location !== window.parent.location
        ? document.referrer
        : document.location.href;
    const refererUrl = dataObj && dataObj.referer ? dataObj.referer : ref;
    dataObj.referer = refererUrl;

    if (dataObj.referer !== document.location.href) {
      setXsollauidFromUrl(Config, url);
    }

    dataObj.url = url;
    send(Config, Config.hitUrl, dataObj);
  };

  /**
   * Tracks external links that the user may click.
   * @param {string} url - The external link address.
   * @param {object} obj - Additional data to be included in the request.
   */
  const externalLink = (url, obj) => {
    if (!Config.isBrowser) {
      return;
    }

    const dataObj = apiHelper(
      Config.externalId,
      Config.extraValues,
      Config.abParams,
      2,
      obj
    );

    dataObj.referer = window.location.href;
    dataObj.url = url;

    send(Config, Config.hitUrl, dataObj);
  };

  /**
   * Gets the value of the Config.dfp property.
   * @returns {string} The value of the Config.dfp property.
   */
  const dfpGet = () => Config.dfp;

  /**
   * Gets the value of the Config.keyDfp property.
   * @returns {string} The value of the Config.keyDfp property.
   */
  const keyDfpGet = () => Config.keyDfp;

  /**
   * Tracks clicks on elements on a page.
   * @param {string} name - The name of the element clicked.
   * @param {object} obj - Additional data to be included in the request.
   */
  const elementClick = (name, obj) => {
    if (!Config.isBrowser) {
      return;
    }

    const dataObj = apiHelper(
      Config.externalId,
      Config.extraValues,
      Config.abParams,
      3,
      obj
    );

    dataObj.url = window.location.href;
    dataObj.en = name;

    send(Config, Config.hitUrl, dataObj);
  };

  /**
   * Sends form data to the server.
   * @param {string} name - The name of the form.
   * @param {object} formData - The form data.
   * @param {object} obj - Additional data to be included in the request.
   */
  const formData = (name, formData, obj) => {
    if (!Config.isBrowser) {
      return;
    }

    const formDataStr = formData ? JSON.stringify(formData) : "";
    const dataObj = apiHelper(
      Config.externalId,
      Config.extraValues,
      Config.abParams,
      4,
      obj
    );
    dataObj.url = window.location.href;
    dataObj.form = formDataStr;
    dataObj.en = name;

    send(Config, Config.hitUrl, dataObj);
  };

  /**
   * Sends a custom event to the server.
   * @param {string} name - The name of the custom event.
   * @param {object} obj - Additional data to be included in the request.
   */
  const customEvent = (name, obj) => {
    if (!Config.isBrowser) {
      return;
    }

    const dataObj = apiHelper(
      Config.externalId,
      Config.extraValues,
      Config.abParams,
      5,
      obj
    );
    dataObj.url = window.location.href;
    dataObj.en = name;

    send(Config, Config.hitUrl, dataObj);
  };

  /**
   * Sends an "LCP" (Largest Contentful Paint) event to the server.
   */
  const eventForLCP = () => {
    if (!Config.isBrowser) {
      return;
    }
    const dataObj = {
      et: 6,
      url: window.location.href,
    };

    send(Config, Config.hitUrl, dataObj);
  };

  /**
   * Sets AB testing parameters.
   * @param {object} obj - An object with parameters.
   */
  const setABParams = (obj) => {
    if (!Config.isBrowser) {
      return;
    }

    Config.abParams = JSON.stringify(obj || "");
  };

  /**
   * Sends custom data to the server.
   * @param {string} route - Request route.
   * @param {object} data - Data to be sent.
   */
  const sendCustomData = (route, data) => {
    let url = route ? `${Config.server}/${route}` : "";
    url && send(Config, url, data);
  };

  /**
   * Sends payment station user session data to the server.
   * @param {object} data - Data to be sent.
   */
  const payStationUserSession = (data) => {
    const url = `${Config.server}/paystation-user-sessions`;
    const body = {
      ...data,
      dfp: Config.dfp,
    };
    sendXHR(Config, url, body);
  };

  Config.hitUrl = `${Config.server}/hit`;

  if (Config.isBrowser) {
    Config.clientId = getUid(Config.counterId);
    Config.visitId = getVid(Config.visitId, Config.counterId);
    transferDataToIframe(Config);
    Config.xsollauid = params.xsollauid || getXsollauid(null, Config.counterId);
    params.abParams && setABParams(params.abParams);
    Config.server && Config.counterId && setHandlers(Config, eventForLCP);
  }

  return {
    hit,
    externalLink,
    elementClick,
    formData,
    dfpGet,
    keyDfpGet,
    customEvent,
    eventForLCP,
    setABParams,
    sendCustomData,
    payStationUserSession,
  };
}
